<template>
  <div
    class="c-app-tabs"
    :class="`c-app-tabs--${variant}`"
  >
    <div
      ref="list"
      class="c-app-tabs__list"
      role="tablist"
      :aria-label="label"
      @keydown="onKeydown"
    >
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        class="c-app-tabs__tab"
        :class="{ 'c-app-tabs__tab--active': tab.key === value }"
        :disabled="tab.disabled"
        :aria-selected="String(tab.key === value)"
        :tabindex="tab.key === value ? 0 : -1"
        @click="select(tab)"
      >
        <app-icon
          v-if="tab.icon"
          :name="tab.icon"
          :size="18"
          :filled="tab.key === value"
        />
        <span>{{ tab.label }}</span>
        <span
          v-if="tab.dot"
          class="c-app-tabs__dot"
          aria-hidden="true"
        />
      </button>
    </div>
  </div>
</template>

<script>
import AppIcon from '../app-icon/AppIcon';

// Lightweight tab navigation (no animated panels): v-model is the active tab key.
export default {
  name: 'AppTabs',
  components: {
    AppIcon,
  },
  props: {
    // [{ key, label, icon?, disabled?, dot? }] — dot marks e.g. "has content"
    tabs: {
      type: Array,
      required: true,
    },
    value: {
      type: [String, Number],
      default: null,
    },
    // 'underline' for page sections, 'pills' for sub-sections / filters
    variant: {
      type: String,
      default: 'underline',
    },
    label: {
      type: String,
      default: 'Sections',
    },
  },
  watch: {
    value() {
      this.$nextTick(this.scrollActiveIntoView);
    },
  },
  mounted() {
    this.scrollActiveIntoView();
  },
  methods: {
    select(tab) {
      if (!tab.disabled && tab.key !== this.value) {
        this.$emit('input', tab.key);
        this.$emit('change', tab);
      }
    },

    // arrow keys move between tabs (WAI-ARIA tabs pattern)
    onKeydown(event) {
      const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];

      if (!step) {
        return;
      }

      event.preventDefault();
      const enabledTabs = this.tabs.filter(tab => !tab.disabled);
      const index = enabledTabs.findIndex(tab => tab.key === this.value);
      const next = enabledTabs[(index + step + enabledTabs.length) % enabledTabs.length];

      if (!next) {
        return;
      }

      this.select(next);
      this.$nextTick(() => {
        const active = this.$refs.list.querySelector('.c-app-tabs__tab--active');
        active && active.focus();
      });
    },

    scrollActiveIntoView() {
      const active = this.$refs.list && this.$refs.list.querySelector('.c-app-tabs__tab--active');

      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.c-app-tabs {
  flex-shrink: 0;
  font-family: var(--font-sans);
}

.c-app-tabs__list {
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.c-app-tabs__tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  background: transparent;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: color var(--duration-fast) ease, background-color var(--duration-fast) ease,
    box-shadow var(--duration-fast) ease;

  &:hover {
    color: var(--text-primary);
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px var(--brand-200);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      color: var(--text-secondary);
      background-color: transparent;
    }
  }
}

.c-app-tabs__dot {
  width: 6px;
  height: 6px;
  margin-left: -2px;
  border-radius: 50%;
  background-color: var(--success-fg);
}

// ---------- underline ----------
.c-app-tabs--underline {
  border-bottom: 1px solid var(--border-default);

  .c-app-tabs__list {
    gap: 4px;
    padding: 0 16px;
  }

  .c-app-tabs__tab {
    height: 48px;
    padding: 0 12px;
    border-radius: var(--radius-sm) var(--radius-sm) 0 0;

    &:hover:not(.c-app-tabs__tab--active) {
      background-color: var(--bg-subtle);
    }
  }

  .c-app-tabs__tab--active {
    color: var(--text-primary);
    font-weight: 600;

    .c-app-icon {
      color: var(--brand-600);
    }

    &:after {
      content: '';
      position: absolute;
      left: 8px;
      right: 8px;
      bottom: -1px;
      height: 2px;
      border-radius: 2px 2px 0 0;
      background-color: var(--brand-600);
    }
  }
}

// ---------- pills ----------
.c-app-tabs--pills {
  .c-app-tabs__list {
    display: inline-flex;
    flex-wrap: wrap;
    max-width: 100%;
    gap: 2px;
    padding: 3px;
    border-radius: var(--radius-md);
    background-color: var(--bg-subtle);
    border: 1px solid var(--border-default);
  }

  .c-app-tabs__tab {
    height: 32px;
    padding: 0 12px;
    border-radius: var(--radius-sm);
  }

  .c-app-tabs__tab--active {
    background-color: var(--bg-surface);
    color: var(--text-primary);
    font-weight: 600;
    box-shadow: var(--shadow-sm);
  }
}
</style>
